import React, { useEffect } from "react";
import { DashboardViewModel } from "../../viewmodels/dashboard/DashboardViewModel";
import PackagingProductionStatsBar from "../../components/stats/PackagingProductionStatsBar";
import { PackagingRecordsViewModel } from "../../viewmodels/packaging/PackagingRecordsViewModel";
import { ManufacturedProductsViewModel } from "../../viewmodels/production/ManufacturedProductsViewModel";

import OpenOrdersCard from "./cards/OpenOrdersCard";
import { formatShortDateTime } from "../../shared/utilityTsx";
import ShippedOrdersCard from "./cards/ShippedOrdersCard";
import InStockTwoMonthsCard from "./cards/InStockTwoMonthsCard";
import RecentProductionCard from "./cards/RecentProductionCard";
import RecentlyPackagedCard from "./cards/RecentlyPackagedCard";
import LowRawInventoryCard from "./cards/LowRawInventoryCard";
import SummaryCards from "./summaryCards/SummaryCards";
import BelowMaxThresholdCard from "./cards/BelowMaxThresholdCard";
import StatsComponents from "../../components/stats/StatsComponents";
import LineChart from "../../components/charts/LineChart";
import BarChart from "../../components/charts/BarChart";

interface DashboardProps {
  logout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ logout }) => {
  const {
    loadingToday,
    packagingToday,
    productionToday,
    getTodayPackagingAndProduction,
  } = DashboardViewModel(logout);

  // instantiate viewmodels for tables (Option A)
  const {
    getPackagings,
    packagings,
    loading: packagingLoading,
    filters: packagingFilters,
  } = PackagingRecordsViewModel(logout);

  const {
    getManufacturedProducts,
    productionHistories,
    loading: manufacturedLoading,
    filters: manufacturedFilters,
  } = ManufacturedProductsViewModel(logout);

  // helper to format numeric values to two decimal places for display
  const formatTwoDecimals = (v: any) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : v;
  };

  // fetch initial dashboard data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    getTodayPackagingAndProduction();
    // fetch initial table pages (first page)
    try {
      // call with existing filters from the viewmodels
      getPackagings && getPackagings(null, 1, packagingFilters);
      getManufacturedProducts &&
        getManufacturedProducts(null, 1, manufacturedFilters);
    } catch (e) {
      // ignore - viewmodels handle errors
    }
  }, []);

  return (
    <div>
      <div className="mt-2 pb-4 px-4 sm:px-6 lg:px-8">
        <div>
          <PackagingProductionStatsBar
            loading={loadingToday}
            packagingToday={packagingToday}
            productionToday={productionToday}
          />
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow p-4">
            <h4 className="text-lg font-medium mb-2">Packaging (24 hours)</h4>
            {packagingLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left">
                      <th className="pb-2">PLU</th>
                      <th className="pb-2">Product</th>
                      <th className="pb-2">Lot</th>
                      <th className="pb-2">Total Packed</th>
                      <th className="pb-2">Total Packaged</th>
                      <th className="pb-2">Packaging Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(((packagings?.data || []) as any[]) || []).length ===
                    0 ? (
                      <tr>
                        <td colSpan={6} className="py-2 text-center text-xl">
                          No recent packaging
                        </td>
                      </tr>
                    ) : (
                      ((packagings?.data || []) as any[])
                        .slice(0, 10)
                        .map((row: any) => (
                          <tr
                            key={row.id || row.lot_number || Math.random()}
                            className="border-t"
                          >
                            <td className="py-2">{row.plu}</td>
                            <td className="py-2">{row.product_name}</td>
                            <td className="py-2">{row.lot_number}</td>
                            <td className="py-2">{row.total_packaged}</td>
                            <td className="py-2">
                              {formatTwoDecimals(row.stock_in_weight)} kg
                            </td>
                            <td className="py-2">
                              {formatShortDateTime(row.packaging_converted)}
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow p-4">
            <h4 className="text-lg font-medium mb-2">Production (24 hours)</h4>
            {manufacturedLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left">
                      <th className="pb-2">PLU</th>
                      <th className="pb-2">Product</th>
                      <th className="pb-2">Lot</th>
                      <th className="pb-2">Total Weight</th>
                      <th className="pb-2">Production Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(((productionHistories?.data || []) as any[]) || [])
                      .length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-2 text-center text-xl">
                          No recent production
                        </td>
                      </tr>
                    ) : (
                      ((productionHistories?.data || []) as any[])
                        .slice(0, 10)
                        .map((row: any) => (
                          <tr
                            key={row.id || row.lot_number || Math.random()}
                            className="border-t"
                          >
                            <td className="py-2">{row.plu}</td>
                            <td className="py-2">{row.product_name}</td>
                            <td className="py-2">{row.lot_number}</td>
                            <td className="py-2">
                              {formatTwoDecimals(
                                row.required_total_raw_material,
                              )}{" "}
                              kg
                            </td>
                            <td className="py-2">
                              {formatShortDateTime(row.production_date)}
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
